// Self-service two-factor authentication: turn 2FA on (QR → verify → recovery
// codes), see remaining codes, regenerate them, or turn it off. Lives in the
// personal Account → Security settings.
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfirm } from '@/features/shared/components/confirm/ConfirmProvider';
import toast from 'react-hot-toast';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { ShieldCheck, Loader2, Copy, Download, Check, KeyRound } from 'lucide-react';
import { getMfaStatus, mfaSetup, mfaEnable, mfaDisable, mfaRegenerateCodes } from '@/lib/hrApi';
import { getApiErrorMessage } from '@/lib/apiError';

type Step = 'idle' | 'setup' | 'codes' | 'disable';

export const TwoFactorCard: React.FC = () => {
  const { t } = useTranslation('settings');
  const confirm = useConfirm();
  const tt = (k: string, dv: string) => t(`twofa.${k}`, { defaultValue: dv });

  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [remaining, setRemaining] = useState(0);
  const [step, setStep] = useState<Step>('idle');
  const [qr, setQr] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [password, setPassword] = useState('');

  const refresh = async () => {
    try {
      const s = await getMfaStatus();
      setEnabled(s.enabled);
      setRemaining(s.backupCodesRemaining);
    } catch { /* leave defaults */ } finally { setLoading(false); }
  };
  useEffect(() => { void refresh(); }, []);

  const startSetup = async () => {
    setBusy(true);
    try {
      const r = await mfaSetup();
      setQr(r.qrCode); setSecret(r.secret); setStep('setup'); setCode('');
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, tt('setup_failed', 'Could not start 2FA setup')));
    } finally { setBusy(false); }
  };

  const verifyEnable = async () => {
    if (code.trim().length < 6) return;
    setBusy(true);
    try {
      const r = await mfaEnable(code.trim());
      setCodes(r.backupCodes); setStep('codes'); setEnabled(true);
      toast.success(tt('enabled', 'Two-factor authentication is on'));
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, tt('bad_code', 'That code didn’t match')));
    } finally { setBusy(false); }
  };

  const regenerate = async () => {
    if (!(await confirm({ title: 'Generate new codes', message: tt('regen_confirm', 'Generate new recovery codes? Your old codes stop working.'), confirmLabel: 'Generate', danger: true }))) return;
    setBusy(true);
    try {
      const r = await mfaRegenerateCodes();
      setCodes(r.backupCodes); setStep('codes');
    } catch (e) { toast.error(getApiErrorMessage(e, tt('generic_err', 'Something went wrong')), { duration: 6000 }); } finally { setBusy(false); }
  };

  const confirmDisable = async () => {
    if (!password) return;
    setBusy(true);
    try {
      await mfaDisable(password);
      setPassword(''); setEnabled(false); setStep('idle'); setCodes(null);
      toast.success(tt('disabled', 'Two-factor authentication is off'));
      void refresh();
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, tt('bad_password', 'Password is incorrect')));
    } finally { setBusy(false); }
  };

  const copyCodes = () => {
    if (!codes) return;
    navigator.clipboard?.writeText(codes.join('\n')).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };
  const downloadCodes = () => {
    if (!codes) return;
    const blob = new Blob([`KaziPeople recovery codes\n\n${codes.join('\n')}\n\nEach code works once. Keep them safe.`], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'kazipeople-recovery-codes.txt'; a.click();
    URL.revokeObjectURL(url);
  };
  const finishCodes = () => { setStep('idle'); setCodes(null); void refresh(); };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5" /> {t('two_factor', { defaultValue: 'Two-Factor Authentication' })}
          {enabled && step === 'idle' && (
            <span className="ml-2 text-xs font-semibold px-2 py-0.5 rounded-full bg-success-soft text-success-strong">{tt('on', 'On')}</span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="py-6 flex items-center gap-2 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin" /> {tt('loading', 'Loading…')}</div>
        ) : step === 'codes' && codes ? (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">{tt('save_codes', 'Save these recovery codes somewhere safe. Each one works once and you won’t see them again.')}</p>
            <div className="grid grid-cols-2 gap-2 rounded-lg border border-border bg-muted/40 p-3 max-w-sm">
              {codes.map((c) => <code key={c} className="text-sm text-foreground text-center tracking-wider py-1">{c}</code>)}
            </div>
            <div className="flex gap-2 max-w-sm">
              <Button variant="outline" className="flex-1 gap-2" onClick={copyCodes}>{copied ? <Check className="w-4 h-4 text-success" /> : <Copy className="w-4 h-4" />} {copied ? tt('copied', 'Copied') : tt('copy', 'Copy')}</Button>
              <Button variant="outline" className="flex-1 gap-2" onClick={downloadCodes}><Download className="w-4 h-4" /> {tt('download', 'Download')}</Button>
            </div>
            <Button onClick={finishCodes}>{tt('done', 'Done')}</Button>
          </div>
        ) : step === 'setup' ? (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">{tt('scan_hint', 'Scan this with an authenticator app (Google Authenticator, Authy…), then enter the 6-digit code.')}</p>
            <div className="flex flex-wrap items-start gap-4">
              {qr && <img src={qr} alt="2FA QR" className="w-40 h-40 rounded-lg border border-border bg-card p-2" />}
              <div className="space-y-2">
                {secret && (
                  <div className="text-xs text-muted-foreground">{tt('manual_key', 'Or enter this key manually:')}<code className="block mt-1 px-2 py-1 rounded bg-muted text-foreground break-all max-w-[14rem]">{secret}</code></div>
                )}
                <div className="relative max-w-[12rem]">
                  <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input autoFocus inputMode="numeric" maxLength={6} value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} placeholder="123456"
                    className="w-full h-11 pl-10 rounded-lg border border-border bg-card text-foreground text-lg tracking-[0.3em] text-center focus:border-orange-400 focus:outline-none" />
                </div>
                <div className="flex gap-2">
                  <Button onClick={verifyEnable} disabled={busy || code.length < 6}>{busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}{tt('verify_enable', 'Verify & enable')}</Button>
                  <Button variant="outline" onClick={() => setStep('idle')} disabled={busy}>{tt('cancel', 'Cancel')}</Button>
                </div>
              </div>
            </div>
          </div>
        ) : step === 'disable' ? (
          <div className="space-y-3 max-w-sm">
            <h4 className="font-medium text-foreground">{tt('disable_title', 'Turn off two-factor authentication?')}</h4>
            <p className="text-sm text-muted-foreground">{tt('disable_hint', 'Enter your account password to confirm. This removes 2FA and your recovery codes.')}</p>
            <input type="password" autoFocus value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') void confirmDisable(); }}
              placeholder={tt('password_ph', 'Account password')}
              className="w-full h-11 px-3 rounded-lg border border-border bg-card text-foreground focus:border-orange-400 focus:outline-none" />
            <div className="flex gap-2">
              <Button variant="outline" className="text-red-600 hover:text-red-700" onClick={confirmDisable} disabled={busy || !password}>
                {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}{tt('turn_off', 'Turn off')}
              </Button>
              <Button variant="outline" onClick={() => { setStep('idle'); setPassword(''); }} disabled={busy}>{tt('cancel', 'Cancel')}</Button>
            </div>
          </div>
        ) : enabled ? (
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h4 className="font-medium text-foreground">{tt('on_title', 'Two-factor authentication is on')}</h4>
              <p className="text-sm text-muted-foreground">{t('twofa.codes_remaining', { defaultValue: '{{n}} recovery codes remaining', n: remaining })}</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={regenerate} disabled={busy}>{tt('regen', 'Regenerate codes')}</Button>
              <Button variant="outline" className="text-red-600 hover:text-red-700" onClick={() => { setPassword(''); setStep('disable'); }} disabled={busy}>{tt('turn_off', 'Turn off')}</Button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h4 className="font-medium text-foreground">{t('2fa_title', { defaultValue: 'Protect your account with 2FA' })}</h4>
              <p className="text-sm text-muted-foreground">{t('2fa_desc', { defaultValue: 'Add a second step at sign-in using an authenticator app.' })}</p>
            </div>
            <Button onClick={startSetup} disabled={busy} className="bg-orange-500 hover:bg-orange-600 text-white">
              {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}{tt('set_up', 'Set up 2FA')}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TwoFactorCard;
